import React, { useCallback, useState } from 'react';
import {
  DropDownButton,
  DropDownButtonItemClickEvent
} from '@progress/kendo-react-buttons';

export type DropDownMenuItem = {
  text: string;
  description?: string;
  children?: DropDownMenuItem[];
};

type KendoReactDropDownButtonProps = {
  buttonLabel: string;
  type: 'primary' | 'secondary' | 'tertiary' | 'text-only' | 'danger';
  menuItems: (string | DropDownMenuItem)[];
  onItemClick?: (event: DropDownButtonItemClickEvent) => void;
  id?: string;
  classNames?: string;
  isDisabled?: boolean;
};

export const KendoReactDropDownButton = (
  props: KendoReactDropDownButtonProps
) => {
  const { buttonLabel, menuItems, onItemClick, isDisabled } = props;
  const [opened, setOpened] = useState(false);

  const handleNestedItemClick = useCallback(
    (child: DropDownMenuItem, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setOpened(false);
      onItemClick?.({ item: child } as DropDownButtonItemClickEvent);
    },
    [onItemClick]
  );

  const handleItemClick = useCallback(
    (event: DropDownButtonItemClickEvent) => {
      const item = event.item;
      if (
        typeof item === 'object' &&
        item !== null &&
        (item as DropDownMenuItem).children?.length
      ) {
        return;
      }
      onItemClick?.(event);
    },
    [onItemClick]
  );

  const itemRender = (itemProps: { item: unknown; itemIndex: number }) => {
    const item = itemProps.item;
    if (typeof item === 'object' && item !== null && 'text' in item) {
      const menuItem = item as DropDownMenuItem;

      if (menuItem.children?.length) {
        return (
          <div className="kendo-dropdown-nested-item">
            <div className="kendo-dropdown-nested-trigger">
              <span>{menuItem.text}</span>
              <i className="fa fa-caret-right" />
            </div>
            <div className="kendo-dropdown-submenu">
              {menuItem.children.map((child) => (
                <button
                  key={child.text}
                  className="kendo-dropdown-submenu-item"
                  data-testid={`dropdown-${child.text
                    .toLowerCase()
                    .replace(/ /g, '-')}`}
                  onClick={(e) => handleNestedItemClick(child, e)}
                >
                  <span className="kendo-dropdown-rich-item-text">
                    {child.text}
                  </span>
                  {child.description && (
                    <span className="kendo-dropdown-rich-item-description">
                      {child.description}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        );
      }

      return (
        <div className="kendo-dropdown-rich-item">
          <span>{menuItem.text}</span>
          {menuItem.description && (
            <span className="kendo-dropdown-rich-item-description">
              {menuItem.description}
            </span>
          )}
        </div>
      );
    }
    return (
      <div
        data-testid={`dropdown-${String(item)
          .toLowerCase()
          .replace(/ /g, '-')}`}
      >{`${String(item)}`}</div>
    );
  };

  return (
    <div className={`kendo-react-drop-down-button ${props.classNames ?? ''}`}>
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      <DropDownButton
        id={props.id}
        disabled={isDisabled}
        items={menuItems}
        text={buttonLabel}
        opened={opened}
        onOpen={() => setOpened(true)}
        onClose={() => setOpened(false)}
        onItemClick={handleItemClick}
        popupSettings={{
          animate: false,
          popupClass: 'kendo-react-drop-down-button-menu'
        }}
        itemRender={itemRender}
        buttonClass={'text-button ' + props.type}
      />
    </div>
  );
};
